import { alpha, Box, BoxProps, Container, Stack, styled, Typography } from '@mui/material'

import { FC, useEffect, useRef, useState } from 'react'

import { AppConfigProvider } from '../../providers'
import { calculateMinutesSinceLastUpdate, calculateMinutesUntilNextUpdate } from './TrailDataFooter.utils'

const StyledFooterBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSticky',
})<
  BoxProps & {
    isSticky?: boolean
  }
>(({ theme, isSticky }) => ({
  bottom: 0,
  ...(isSticky && {
    background: alpha('#000', 0.75),
    boxShadow: theme.shadows[4],
  }),
  marginTop: theme.spacing(2),
  position: 'sticky',
  width: '100%',
  zIndex: 1000,
}))

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}))

const ExtraDescriptor = styled('span')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}))

// Sits in normal flow directly under the footer and takes the place of its bottom margin
const FooterSentinel = styled('div')(({ theme }) => ({
  height: theme.spacing(1),
}))

export const TrailDataFooter: FC = () => {
  const footerContainerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [isSticky, setIsSticky] = useState(false)
  const [now, setNow] = useState(() => new Date())
  const minutesSinceLastUpdate = calculateMinutesSinceLastUpdate(now)
  const minutesUntilNextUpdate = calculateMinutesUntilNextUpdate(now)

  useEffect(() => {
    const intervalId = setInterval(() => setNow(new Date()), 60 * 1000) // 1 minute
    return () => clearInterval(intervalId)
  }, [])

  useEffect(() => {
    const footerContainer = footerContainerRef.current
    const sentinel = sentinelRef.current
    if (!footerContainer || !sentinel) {
      return undefined
    }
    // When position: sticky lifts the footer off its natural spot, a gap opens between it and the
    // sentinel. Measuring that gap, rather than intersecting with the viewport edge, stays correct on
    // browsers whose viewport (e.g. with collapsing toolbars) differs from where sticky pins the bar.
    const updateIsSticky = () => {
      setIsSticky(sentinel.getBoundingClientRect().top - footerContainer.getBoundingClientRect().bottom > 0.5)
    }
    updateIsSticky()
    // Content height changes (cards loading, filters) move the footer without a scroll event
    const resizeObserver = new ResizeObserver(updateIsSticky)
    resizeObserver.observe(document.body)
    window.addEventListener('scroll', updateIsSticky, { passive: true })
    window.addEventListener('resize', updateIsSticky)
    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('scroll', updateIsSticky)
      window.removeEventListener('resize', updateIsSticky)
    }
  }, [])

  return (
    <>
      <StyledFooterBox ref={footerContainerRef} isSticky={isSticky}>
        <StyledContainer maxWidth="xl">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack alignItems="center">
              <Typography variant="body1" color="grey" fontSize={{ sm: 16, xs: 12 }}>
                {minutesSinceLastUpdate} mins
              </Typography>
              <Typography variant="caption" color="grey">
                <ExtraDescriptor>since&nbsp;</ExtraDescriptor>last update
              </Typography>
            </Stack>
            {!!AppConfigProvider.notification && (
              <Stack
                alignItems="center"
                direction="row"
                justifyContent="center"
                dangerouslySetInnerHTML={{ __html: AppConfigProvider.notification }}
              ></Stack>
            )}
            <Stack alignItems="center">
              <Typography variant="body1" fontSize={{ sm: 16, xs: 12 }}>
                {minutesUntilNextUpdate} mins
              </Typography>
              <Typography variant="caption">
                <ExtraDescriptor>until&nbsp;</ExtraDescriptor>next update
              </Typography>
            </Stack>
          </Stack>
        </StyledContainer>
      </StyledFooterBox>
      <FooterSentinel ref={sentinelRef} />
    </>
  )
}
TrailDataFooter.displayName = 'TrailDataFooter'
